/**
 * Lightweight frontend error reporter.
 *
 * Why: Facebook/Instagram in-app webviews run older WebKit/Chromium engines
 * that silently crash on modern JS. Without server-side visibility we only
 * learn about it when a prospect emails support. This module ships every
 * runtime error, unhandled promise rejection, and ErrorBoundary catch to
 * `/api/_fe_errors` so we can see them in the admin audit view.
 *
 * Design:
 *  - Dedupes identical messages within a 10s window (one tab).
 *  - Hard cap of 20 reports per page load — a broken render that spams
 *    errors must not hammer the backend.
 *  - fetch(..., keepalive:true) so the report survives navigation /
 *    unload. Falls back to no-op on fetch failure (never throw from here).
 *  - Never sends PII — only message, stack, url, UA, viewport.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const ENDPOINT = `${BACKEND_URL}/api/_fe_errors`;

const _recent = new Map(); // key -> timestamp (ms)
const DEDUPE_WINDOW_MS = 10_000;
const MAX_PER_SESSION = 20;
let _sent = 0;

function _ua() {
  try { return navigator.userAgent || ""; } catch { return ""; }
}

function _viewport() {
  try {
    return {
      w: window.innerWidth || 0,
      h: window.innerHeight || 0,
      dpr: window.devicePixelRatio || 1,
    };
  } catch {
    return { w: 0, h: 0, dpr: 1 };
  }
}

function _shouldDrop(key) {
  const now = Date.now();
  // garbage-collect old entries
  for (const [k, ts] of _recent) {
    if (now - ts > DEDUPE_WINDOW_MS) _recent.delete(k);
  }
  if (_recent.has(key)) return true;
  _recent.set(key, now);
  return false;
}

export function reportFrontendError(payload) {
  try {
    if (_sent >= MAX_PER_SESSION) return;
    const kind = payload?.kind || "unknown";
    const message = String(payload?.message || "").slice(0, 500);
    const stack = String(payload?.stack || "").slice(0, 2000);
    const key = `${kind}|${message}`;
    if (!message) return;
    if (_shouldDrop(key)) return;
    _sent += 1;
    const body = JSON.stringify({
      kind,
      message,
      stack,
      component_stack: String(payload?.componentStack || "").slice(0, 2000),
      url: (typeof window !== "undefined" && window.location && window.location.href) || "",
      user_agent: _ua(),
      viewport: _viewport(),
      ts: new Date().toISOString(),
    });
    // keepalive lets us log just before navigation/unload
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      credentials: "omit",
      keepalive: true,
    }).catch(() => {});
  } catch {
    // never throw from the reporter
  }
}

export function installGlobalErrorHandlers() {
  if (typeof window === "undefined") return;
  if (window.__ppt_fe_errors_installed) return;
  window.__ppt_fe_errors_installed = true;

  window.addEventListener("error", (e) => {
    // Suppress the known PerformanceServerTiming DataCloneError that we
    // already swallow in index.html — don't log it as a real error.
    if (
      e &&
      e.error &&
      e.error instanceof DOMException &&
      e.error.name === "DataCloneError" &&
      e.message &&
      e.message.includes("PerformanceServerTiming")
    ) {
      return;
    }
    reportFrontendError({
      kind: "window.error",
      message: (e && (e.message || (e.error && e.error.message))) || "",
      stack: (e && e.error && e.error.stack) || "",
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e && e.reason;
    const message =
      (reason && (reason.message || String(reason))) || "unhandledrejection";
    reportFrontendError({
      kind: "promise.unhandledrejection",
      message,
      stack: (reason && reason.stack) || "",
    });
  });
}
